import React, {useState, useEffect} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Container, Row, Col, Card, Button, Spinner, Alert, Modal} from 'react-bootstrap';
import {FaArrowLeft, FaArrowRight, FaSave} from 'react-icons/fa';
import {toast} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Import steps
import Step1BasicInfo from './steps/Step1BasicInfo';
import Step2Shafyors from './steps/Step2Shafyors';
import Step3Priseps from './steps/Step3Priseps';
import Step4Yonalishlar from './steps/Step4Yonalishlar';

// Import services
import TransportService from '../../services/TransportService';
import ShafyorService from '../../services/ShafyorService';
import PrisepsService from '../../services/PrisepsService';
import PutyovkaYonalish from '../../services/PutyovkaYonalish';
import PutyovkaService from '../../services/PutyovkaService';

const PutyovkaWizard = ({
                            isEdit = false, transportId, doNotNavigate = false, onClose = () => {
    }
                        }) => {
    const {id} = useParams();
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [put, setPut] = useState('');
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [closeModal, setCloseModal] = useState(false);
    const [muddatUzaytirish, setMuddatUzaytirish] = useState(false);

    // Form data state
    const [formData, setFormData] = useState({
        transport_id: transportId || '',
        raqam: '',
        narx: '',
        dan: new Date(),
        gacha: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 7 days from now
        shafyors: [],
        putyofka_priseps: [],
        putyofka_yonalishs: []
    });

    // Options for dropdowns
    const [options, setOptions] = useState({
        transports: [],
        shafyors: [],
        availableShafyors: [],  // Initialize availableShafyors as an empty array
        priseps: [],
        yonalishlar: []
    });

    // Handle form data update with useCallback to prevent unnecessary re-renders
    const updateFormData = React.useCallback((newData) => {
        console.log('Updating form data with:', newData);

        // If newData is a function, call it with the current state
        if (typeof newData === 'function') {
            setFormData(prev => {
                const updatedData = newData(prev);
                console.log('Updated form data (function update):', updatedData);
                return {...prev, ...updatedData};
            });
        } else {
            // If newData is an object, merge it with the current state
            setFormData(prev => {
                console.log('Updated form data (object update):', {...prev, ...newData});
                return {...prev, ...newData};
            });
        }
    }, []);  // Empty dependency array since we don't use any external values

    // Load initial data
    useEffect(() => {
        let isMounted = true;

        const loadInitialData = async () => {
            try {
                setLoading(true);

                // Load all required data in parallel
                const [
                    transportsRes, 
                    shafyorsRes, 
                    prisepsRes, 
                    yonalishlarRes,
                    lastPutyovkaRes
                ] = await Promise.all([
                    TransportService.getTransportList(),
                    ShafyorService.searchByName(''), // Empty string to get all shafyors
                    PrisepsService.searchByName(),
                    PutyovkaYonalish.getYonalishList(),
                    PutyovkaService.getLastIndex()
                ]);
                
                // Get the last putyovka number and increment by 1 for the new putyovka
                const lastPutyovkaNumber = lastPutyovkaRes?.data?.raqam + 1 || '';

                // Only update state if component is still mounted
                if (isMounted) {
                    // Create new options object
                    const newOptions = {
                        transports: transportsRes.data.map(t => ({
                            value: t.id,
                            label: `${t.name || ''} ${t.davlat_raqam ? `(${t.davlat_raqam})` : ''}`.trim()
                        })),
                        shafyors: shafyorsRes.data.map(s => ({
                            value: s.id,
                            label: `${s.name} (${s.prava_raqam})`,
                            ...s
                        })),
                        priseps: prisepsRes.data.map(p => ({
                            value: p.id,
                            label: `${p.name || ''} (${p.davlat_raqam})`,
                            ...p
                        })),
                        yonalishlar: yonalishlarRes.data.map(y => ({
                            value: `${y.from_davlat}->${y.to_davlat}`,
                            label: `${y.from_davlat} -> ${y.to_davlat}`,
                            from_davlat: y.from_davlat,
                            to_davlat: y.to_davlat
                        }))
                    };

                    // Only update if options have changed
                    setOptions(prevOptions =>
                        JSON.stringify(prevOptions) === JSON.stringify(newOptions)
                            ? prevOptions
                            : newOptions
                    );
                }

                // If editing, load existing putyovka data
                if (isEdit && id) {
                    const putyovkaRes = await PutyovkaService.getPutyovkaById(id);
                    const data = putyovkaRes.data;
                    let resData = transformPutyovkaData(data);
                    console.log('response data ga keldi', data)
                    setFormData({
                        ...resData,
                        dan: new Date(resData.dan),
                        gacha: new Date(resData.gacha),
                        narx: resData.narx.toString()
                    });
                } else {
                    // Set the new putyovka number for new putyovka
                    setFormData(prev => ({
                        ...prev,
                        raqam: lastPutyovkaNumber
                    }));
                }

            } catch (error) {
                console.error('Error loading initial data:', error);
                toast.error('Maʼlumotlarni yuklashda xatolik yuz berdi');
            } finally {
                setLoading(false);
            }
        };

        loadInitialData();

        // Cleanup function
        return () => {
            isMounted = false;
        };
    }, [id, isEdit]);  // Removed updateFormData from dependencies since it's now defined above

    const transformPutyovkaData = (data) => {
        return {
            transport_id: data.transport_id || '',
            raqam: data.raqam || '',
            narx: data.narx?.toString() || '',
            dan: data.dan ? new Date(data.dan) : new Date(),
            gacha: data.gacha ? new Date(data.gacha) : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),

            shafyors: (data.shafyors || []).map(item => ({
                ...item.shafyor, // foydalanuvchining barcha fieldlari (name, phone, prava_raqam, ...)
                id: item.shafyor.id,
                user_id: item.user_id,
                putyofka_id: item.putyofka_id,
                created_at: item.created_at,
                updated_at: item.updated_at
            })),

            putyofka_priseps: (data.putyofka_priseps || []).map(item => ({
                ...item.transport, // transportdagi barcha fieldlar
                id: item.transport.id,
                transport_id: item.transport_id,
                putyofka_id: item.putyofka_id,
                created_at: item.created_at,
                updated_at: item.updated_at
            })),

            putyofka_yonalishs: (data.putyofka_yonalishs || []).map(item => ({
                ...item
            }))
        };
    };

    const handlePdfGenerate = async (item, global) => {
        if (!item?.id) {
            toast.error('Fayl olishda xatolik');
            return;
        }

        const token = localStorage.getItem('token');
        const url = `https://logistika.smart-trade.uz/api/pdf_generator/putyofka/${item.id}?global=${global}&token=${token}`;

        window.open(url, '_blank');
        // setShowDownloadModal(false);

    };

    useEffect(() => {
      if (closeModal){
          if (!doNotNavigate) {
              navigate('/dashboard/putyovka');
          } else {
              onClose();
          }
      }
    })
    // Navigate to next step
    const nextStep = () => {
        setCurrentStep(prev => Math.min(prev + 1, 4));
    };

    // Navigate to previous step
    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    // Handle form submission
    const validateForm = () => {
        const errors = [];

        // Check required fields
        if (!formData.transport_id) {
            errors.push('Transport tanlanmagan');
        }

            // else if (!formData.raqam || formData.raqam.trim() === '') {
            //   errors.push('Putyovka raqami kiritilmagan');
        // }

        else if (!formData.narx || isNaN(parseFloat(formData.narx))) {
            errors.push('Narx noto\'g\'ri kiritilgan');
        } else if (!formData.dan) {
            errors.push('Boshlanish sanasi kiritilmagan');
        } else if (!formData.gacha) {
            errors.push('Tugash sanasi kiritilmagan');
        }

        // Check at least one shafyor is selected
        else if (!formData.shafyors || formData.shafyors.length === 0) {
            errors.push('Kamida bitta shafyor tanlanishi shart');
        }

        // Check at least one prisep is selected
        else if (!formData.putyofka_priseps || formData.putyofka_priseps.length === 0) {
            errors.push('Kamida bitta prisyopka tanlanishi shart');
        }

        // Check at least one yonalish is added
        else if (!formData.putyofka_yonalishs || formData.putyofka_yonalishs.length === 0) {
            errors.push('Kamida bitta yo\'nalish qo\'shilishi shart');
        }

        return errors;
    };

    const handleSubmit = async () => {
        try {
            // Validate form
            const validationErrors = validateForm();
            if (validationErrors.length > 0) {
                validationErrors.forEach(error => {
                    toast.error(error);
                });
                return;
            }

            setSaving(true);

            // Prepare data for submission
            const submitData = {
                ...formData,
                narx: parseFloat(formData.narx),
                dan: formData.dan.toISOString().split('T')[0],
                gacha: formData.gacha.toISOString().split('T')[0],
                raqam: muddatUzaytirish ? '' : formData.raqam
            };

            if (isEdit && id && !muddatUzaytirish) {
                console.log('update ga keldikuuuuuuuuuuuuuuuuuuuuuuuuu')
                const res = await PutyovkaService.updatePutyovka(submitData, id);
                if (res && res.success === true) {
                    toast.success('Putyovka muvaffaqiyatli yangilandi');
                    navigate('/dashboard/putyovka');
                }else return;
            } else {
                console.log('submitData', submitData);
                let res = await PutyovkaService.addPutyovka(submitData);
                console.log('putyovka res', res);
                if (res?.success === true) {
                    setPut(res.data);
                    setShowDownloadModal(true);
                    toast.success('Putyovka muvaffaqiyatli yaratildi');
                } else {
                    return
                }
            }

            // if (!doNotNavigate) {
            //     navigate('/dashboard/putyovka');
            // } else {
            //     onClose();
            // }
        } catch (error) {
            console.error('Error saving putyovka:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Putyovkani saqlashda xatolik yuz berdi';
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    // Render current step
    const renderStep = () => {
        if (loading) {
            return (
                <div className="text-center py-5">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Yuklanmoqda...</span>
                    </Spinner>
                    <p className="mt-2">Maʼlumotlar yuklanmoqda...</p>
                </div>
            );
        }

        const commonProps = {
            formData,
            updateFormData,
            options,
            setOptions,
            isEdit
        };

        switch (currentStep) {
            case 1:
                return <Step1BasicInfo {...commonProps} transportId={transportId} muddatUzaytirish={muddatUzaytirish} setMuddatUzaytirish={setMuddatUzaytirish}/>;
            case 2:
                return <Step2Shafyors {...commonProps} />;
            case 3:
                return <Step3Priseps {...commonProps} />;
            case 4:
                return <Step4Yonalishlar {...commonProps} />;
            default:
                return null;
        }
    };

    // Step titles
    const stepTitles = [
        'Asosiy maʼlumotlar',
        'Shafyorlar',
        'Priseplar',
        'Yoʻnalishlar'
    ];

    return (
        <Container fluid className="py-4">
            <Card className="mb-4">
                <Card.Header className="bg-light">
                    <h4 className="mb-0">
                        {isEdit ? 'Putyovkani tahrirlash' : 'Yangi putyovka yaratish'}
                    </h4>
                </Card.Header>
                <Card.Body>
                    {/* Progress Steps */}
                    <div className="pw-wizard-progress mb-4">
                        <div className="d-flex justify-content-between">
                            {stepTitles.map((title, index) => (
                                <div
                                    key={index}
                                    className={`pw-step ${currentStep > index + 1 ? 'pw-completed' : ''} ${currentStep === index + 1 ? 'pw-active' : ''}`}
                                >
                                    <div className="pw-step-number">{index + 1}</div>
                                    <div className="pw-step-title">{title}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Current Step Content */}
                    {renderStep()}

                    {/* Navigation Buttons */}
                    <div className="d-flex justify-content-between mt-4">
                        <Button
                            variant="outline-secondary"
                            onClick={currentStep === 1 ? () => navigate('/dashboard/putyovka') : prevStep}
                            disabled={saving}
                        >
                            <FaArrowLeft className="me-1"/>
                            {currentStep === 1 ? 'Bekor qilish' : 'Orqaga'}
                        </Button>

                        {(isEdit || currentStep === 4) && (
                            <Button
                                variant="success"
                                onClick={handleSubmit}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2"/>
                                        Saqlanmoqda...
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="me-1"/>
                                        Saqlash
                                    </>
                                )}
                            </Button>
                        )}

                        {/*{currentStep < 4 ? (*/}
                        {currentStep < 4 && (
                            <Button
                                variant="outline-primary"
                                onClick={nextStep}
                                disabled={saving}
                            >
                                Keyingisi <FaArrowRight className="ms-1"/>
                            </Button>
                        )}

                        {/*<Button*/}
                        {/*    variant="success"*/}
                        {/*    onClick={handleSubmit}*/}
                        {/*    disabled={saving}*/}
                        {/*>*/}
                        {/*  {saving ? (*/}
                        {/*      <>*/}
                        {/*        <Spinner animation="border" size="sm" className="me-2" />*/}
                        {/*        Saqlanmoqda...*/}
                        {/*      </>*/}
                        {/*  ) : (*/}
                        {/*      <>*/}
                        {/*        <FaSave className="me-1" />*/}
                        {/*        Saqlash*/}
                        {/*      </>*/}
                        {/*  )}*/}
                        {/*</Button>*/}
                        {/*/!*) : (*!/*/}
                        {/*<Button*/}
                        {/*    variant="outline-primary"*/}
                        {/*    onClick={nextStep}*/}
                        {/*    disabled={saving}*/}
                        {/*>*/}
                        {/*  Keyingisi <FaArrowRight className="ms-1" />*/}
                        {/*</Button>*/}

                        {/*)}*/}
                    </div>

                </Card.Body>
            </Card>

            {/* Download Options Modal */}
            <Modal show={showDownloadModal}
                   style={{zIndex: 9999}}
                   onHide={() => {
                       setShowDownloadModal(false);
                       setCloseModal(true);
                   }} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Putyovka yuklab olish</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <p>Putyovkani qanday yuklab olishni xohlaysiz?</p>
                    <div className="d-flex justify-content-center gap-3 mt-4">
                        <Button
                            variant="primary"
                            onClick={() => handlePdfGenerate(put, 0)}
                            className="px-4"
                        >
                            Oddiy yuklab olish
                        </Button>
                        <Button
                            variant="success"
                            onClick={() => handlePdfGenerate(put, 1)}
                            className="px-4"
                        >
                            Global yuklab olish
                        </Button>
                    </div>
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default PutyovkaWizard;
